import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Avatar } from "antd";
import "./styles.css";
import {
  IdcardOutlined,
  HomeOutlined,
  TeamOutlined,
  SettingOutlined,
  LogoutOutlined,
  SafetyCertificateOutlined,
  DownOutlined,
  UserOutlined,
  CloseOutlined,
} from "@ant-design/icons";

// Configuração do menu para Admin
const adminMenuConfig = [
  {
    key: "inicio",
    label: "Início",
    icon: HomeOutlined,
    path: "/admin/dashboard",
  },
  {
    key: "clientes",
    label: "Clientes",
    icon: TeamOutlined,
    submenu: [
      { label: "Criar Novo", path: "/admin/cadastro-cliente" },
      { label: "Listar Clientes", path: "/admin/clientes" },
    ],
  },
  {
    key: "usuarios",
    label: "Usuários",
    icon: IdcardOutlined,
    submenu: [
      { label: "Criar Novo", path: "/admin/cadastro-usuario" },
      { label: "Listar Usuários", path: "/admin/usuarios" },
    ],
  },
  {
    key: "valvulas",
    label: "Válvulas",
    icon: SettingOutlined,
    submenu: [
      { label: "Criar Nova", path: "/admin/cadastro-valvula" },
      { label: "Listar Válvulas", path: "/admin/valvulas" },
    ],
  },
  {
    key: "certificados",
    label: "Certificados",
    icon: SafetyCertificateOutlined,
    submenu: [
      { label: "Criar Novo", path: "/admin/cadastro-certificado" },
      { label: "Listar Certificados", path: "/admin/certificados" },
    ],
  },
];

// Configuração do menu para Cliente
const clientMenuConfig = [
  {
    key: "inicio",
    label: "Início",
    icon: HomeOutlined,
    path: "/cliente/dashboard",
  },
  {
    key: "valvulas",
    label: "Válvulas",
    icon: SettingOutlined,
    path: "/cliente/valvulas",
  },
  {
    key: "certificados",
    label: "Certificados",
    icon: SafetyCertificateOutlined,
    path: "/cliente/certificados",
  },
];

function Sidebar({ userType = "admin", mobileMenuOpen = false, onCloseMobileMenu }) {
  const [openSubmenus, setOpenSubmenus] = useState({});
  const [usuario, setUsuario] = useState({});
  const navigate = useNavigate();
  const location = useLocation();

  // Busca dados do usuário
  useEffect(() => {
    const usuarioData = JSON.parse(sessionStorage.getItem("seepilUsuario"));
    setUsuario(usuarioData || {});
  }, []);

  // Seleciona o menu baseado no tipo de usuário
  const menuConfig = userType === "cliente" ? clientMenuConfig : adminMenuConfig;

  const getInitials = (name) => {
    if (!name) return "U";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .substring(0, 2)
      .toUpperCase();
  };

  function logout() {
    sessionStorage.clear();
    localStorage.clear();
    navigate("/login");
  }

  const toggleSubmenu = (key) => {
    setOpenSubmenus((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const isActive = (path) => location.pathname === path;

  const isSubmenuActive = (submenu) => {
    if (!submenu) return false;
    return submenu.some((item) => location.pathname.includes(item.path));
  };

  return (
    <aside className={`sidebar ${mobileMenuOpen ? "mobile-open" : ""}`}>
      {/* Botão fechar no mobile */}
      <button className="mobile-close-btn" onClick={onCloseMobileMenu}>
        <CloseOutlined />
      </button>

      {/* Logo */}
      <div className="logo-container">
        <div className="logo-box">
          <img
            src="/assets/logo-branca1.png"
            alt="Seepil Valve Care"
            className="logo-image"
          />
        </div>
      </div>

      {/* Perfil do usuário (visível no mobile) */}
      <div className="sidebar-user-info">
        <Avatar
          size={48}
          src="/assets/avatar.jpg"
          icon={<UserOutlined />}
          className="sidebar-user-avatar"
        >
          {getInitials(usuario.nomeUsuario)}
        </Avatar>
        <div className="sidebar-user-details">
          <span className="sidebar-user-name">
            {usuario.nomeUsuario || "Usuário"}
          </span>
          <span className="sidebar-user-role">
            {usuario.nomeEmpresa || "Seepil"}
          </span>
        </div>
      </div>

      {/* Menu */}
      <nav className="nav-menu">
        <ul className="sidebar-menu">
          {menuConfig.map((item) => (
            <li key={item.key}>
              {item.submenu ? (
                <>
                  <button
                    className={`menu-item ${
                      isSubmenuActive(item.submenu) ? "active" : ""
                    }`}
                    onClick={() => toggleSubmenu(item.key)}
                  >
                    <item.icon className="menu-icon" />
                    <span>{item.label}</span>
                    <DownOutlined
                      className={`arrow-icon ${
                        openSubmenus[item.key] ? "open" : ""
                      }`}
                    />
                  </button>
                  <ul
                    className={`submenu ${
                      openSubmenus[item.key] ? "open" : ""
                    }`}
                  >
                    {item.submenu.map((subItem) => (
                      <li key={subItem.path}>
                        <Link
                          to={subItem.path}
                          className={isActive(subItem.path) ? "active" : ""}
                        >
                          <span className="submenu-dot"></span>
                          {subItem.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </>
              ) : (
                <Link
                  to={item.path}
                  className={`menu-item ${
                    isActive(item.path) ? "active" : ""
                  }`}
                >
                  <item.icon className="menu-icon" />
                  <span>{item.label}</span>
                </Link>
              )}
            </li>
          ))}
        </ul>
      </nav>

      {/* Logout */}
      <div className="sidebar-footer">
        <button onClick={logout} className="logout-button">
          <LogoutOutlined className="menu-icon" />
          <span>Logout</span>
        </button>
      </div>
    </aside>
  );
}

export default Sidebar;
